"use client"
import { Phone, Clock, AlertTriangle, Shield } from "lucide-react"

export default function EmergencyAfterHours() {
  const emergencyServices = [
    {
      service: "Emergency Dispatch",
      phone: "(800) 555-HELP",
      description: "24/7 emergency response coordination",
      available: "24/7",
      priority: "high",
    },
    {
      service: "After Hours Support",
      phone: "(800) 555-0911",
      description: "Weekend and holiday support services",
      available: "Weekends & Holidays",
      priority: "medium",
    },
    {
      service: "Emergency Repairs",
      phone: "(800) 555-FIX1",
      description: "Urgent repair and maintenance services",
      available: "24/7",
      priority: "high",
    },
    {
      service: "Customer Emergency Line",
      phone: "(800) 555-9999",
      description: "Direct customer emergency assistance",
      available: "24/7",
      priority: "high",
    },
  ]

  const getPriorityIcon = (priority: string) => {
    return priority === "high" ? (
      <AlertTriangle className="h-5 w-5 text-red-500" />
    ) : (
      <Shield className="h-5 w-5 text-yellow-500" />
    )
  }

  const getPriorityBadge = (priority: string) => {
    return priority === "high" ? (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
        Critical
      </span>
    ) : (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
        Standard
      </span>
    )
  }

  return (
    <div className="p-4 lg:p-6">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl lg:text-3xl font-bold text-[#004261] mb-2 uppercase tracking-wide">
            Emergency After Hours
          </h1>
          <div className="w-16 h-1 bg-[#00CCFE] mb-4"></div>
          <p className="text-gray-600">24/7 emergency contact information and after-hours support services</p>
        </div>

        {/* Emergency Alert */}
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-red-400 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-red-800">Emergency Situations</h3>
              <p className="text-sm text-red-700 mt-1">
                For life-threatening emergencies, call 911 immediately. Use the numbers below for service-related
                emergencies only.
              </p>
            </div>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {emergencyServices.map((service, index) => (
            <div
              key={index}
              className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-shadow"
            >
              <div className="bg-[#004261] px-6 py-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-white font-bold text-lg">{service.service}</h3>
                  {getPriorityIcon(service.priority)}
                </div>
              </div>

              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <Phone className="h-5 w-5 text-[#00CCFE] mr-2" />
                    <a
                      href={`tel:${service.phone}`}
                      className="text-xl font-bold text-[#00CCFE] hover:text-[#004261] hover:underline transition-colors"
                    >
                      {service.phone}
                    </a>
                  </div>
                  {getPriorityBadge(service.priority)}
                </div>

                <p className="text-gray-600 mb-4">{service.description}</p>

                <div className="flex items-center text-sm text-gray-500">
                  <Clock className="h-4 w-4 text-[#00CCFE] mr-2" />
                  <span className="font-medium">Available: {service.available}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Information */}
        <div className="mt-8 bg-blue-50 border border-[#00CCFE] rounded-lg p-6">
          <h3 className="text-lg font-bold text-[#004261] mb-4 uppercase">Important Information</h3>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <h4 className="font-medium text-[#004261] mb-2">Response Times</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Critical emergencies: 15-30 minutes</li>
                <li>• Standard after-hours: 1-2 hours</li>
                <li>• Weekend service: 2-4 hours</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-[#004261] mb-2">What to Have Ready</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Customer account number</li>
                <li>• Location address</li>
                <li>• Description of the issue</li>
                <li>• Contact phone number</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
